import React, { createContext, useContext, useMemo } from 'react';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import type { AppActionConfig, Message, MessageButtonSpec, SourceRef } from '@captivateiq/assistant/types';
import {
  FEATURE_FLAG_AGENT_CHARTING,
  FEATURE_FLAG_AI_COMP_ADMIN_AGENT,
  isFeatureEnabled,
} from '@captivateiq/assistant/utils/featureFlags';
import { QBox } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';
import { FeedbackButtons } from '../../../shared/FeedbackButtons';
import { AppActionLabel } from './AppActionLabel';
import { ContentBlockRenderer } from './ContentBlocks/ContentBlockRenderer';
import { MessageButtonBar } from './MessageButtonBar';
import { SourceCitationChip } from './SourceCitationChip';
import { SourcesFooter } from './SourcesFooter';
import { expandedTextColumnSx } from '../../shared/expandedTextColumnSx';
import { injectCitationMarkers } from '../../../../utils/citations';

type AgentMessageProps = {
  message: Message;
  buttons?: MessageButtonSpec[];
  appActions?: AppActionConfig[];
  feedback?: number | null;
  feedbackResponse?: string | null;
  onFeedback?: (messageId: string, feedback: number) => void;
  onFeedbackResponse?: (messageId: string, text: string) => Promise<void>;
};

const sanitizationSchema = {
  ...defaultSchema,
  tagNames: [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    'del',
    's',
    'mark',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'a',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'th',
    'td',
    'blockquote',
    'hr',
    'code',
    'pre',
    'span',
    'sup',
  ],
  attributes: {
    // Inherits from hast-util-sanitize's defaultSchema — verify its changelog when upgrading rehype-sanitize
    ...defaultSchema.attributes,
    span: ['className'],
    sup: ['className', 'dataSourceIndex'],
  },
};

const defaultMarkdownComponents = {
  table: ({ children }: React.TableHTMLAttributes<HTMLTableElement>) => (
    <QBox
      sx={{
        width: '100%',
        overflowX: 'auto',
        marginBottom: '0.5rem',
        scrollbarWidth: 'thin',
        // Horizontal scroll shadows with theme color
        backgroundImage: `
          linear-gradient(90deg, rgba(251,251,251,1) 30%, rgba(251,251,251,0)),
          linear-gradient(90deg, rgba(251,251,251,0), rgba(251,251,251,1) 70%),
          radial-gradient(farthest-side at 0% 50%, rgba(0,0,0,0.1), rgba(0,0,0,0)),
          radial-gradient(farthest-side at 100% 50%, rgba(0,0,0,0.1), rgba(0,0,0,0))
        `,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '30px 100%, 30px 100%, 10px 100%, 10px 100%',
        backgroundPosition: '0 0, 100% 0, 0 0, 100% 0',
        backgroundAttachment: 'local, local, scroll, scroll',
        '.statement-elevated-section &': {
          // on white background
          backgroundImage: `
          linear-gradient(90deg, rgba(255,255,255,1) 30%, rgba(255,255,255,0)),
          linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,1) 70%),
          radial-gradient(farthest-side at 0% 50%, rgba(0,0,0,0.1), rgba(0,0,0,0)),
          radial-gradient(farthest-side at 100% 50%, rgba(0,0,0,0.1), rgba(0,0,0,0))
        `,
        },
        '& table': {
          width: '100%',
          borderCollapse: 'collapse',
        },
      }}
    >
      <table>{children}</table>
    </QBox>
  ),
};

const SourcesContext = createContext<SourceRef[]>([]);

const SupWithCitation = ({ children, node, ...props }: React.HTMLAttributes<HTMLElement> & { node?: unknown }) => {
  const sources = useContext(SourcesContext);
  const sourceIndex = (props as Record<string, unknown>)['data-source-index'];
  if (sourceIndex !== undefined && sources.length > 0) {
    const idx = Number(sourceIndex);
    const sourceRef = sources[idx - 1];
    if (sourceRef) {
      return (
        <SourceCitationChip
          dataContext={sourceRef.data_context}
          index={idx}
          label={sourceRef.label}
          source={sourceRef.url}
        />
      );
    }
  }
  return <sup {...props}>{children}</sup>;
};

const markdownComponents = {
  ...defaultMarkdownComponents,
  sup: SupWithCitation,
};

export const AgentMessage = ({
  message,
  buttons = [],
  appActions = [],
  feedback,
  feedbackResponse,
  onFeedback,
  onFeedbackResponse,
}: AgentMessageProps) => {
  const { features, isExpanded } = useAIShellContext();
  const citationChipsEnabled = features.citationChips ?? false;
  const sources = useMemo(
    () => (citationChipsEnabled ? message.cited_sources ?? [] : []),
    [citationChipsEnabled, message.cited_sources],
  );

  const processedContents = useMemo(
    () => injectCitationMarkers(message.contents, sources.length),
    [message.contents, sources.length],
  );
  return (
    <>
      <AppActionLabel actions={appActions} />
      {buttons?.length > 0 && <MessageButtonBar align="right" buttons={buttons} />}
      <QBox
        data-testid="agent-message"
        sx={{
          textAlign: 'left',
          fontSize: '13px',
          lineHeight: 1.5,
          overflowWrap: 'break-word',
          color: 'grey.700',
          pb: 3,
          ...expandedTextColumnSx(isExpanded),
          '& a': {
            color: 'primary.main',
            textDecoration: 'underline',
            textUnderlineOffset: '2px',
            '&:hover': { color: 'primary.dark' },
          },
          '& p': {
            margin: '0 0 0.75rem 0',
            '&:last-child': {
              marginBottom: 0,
            },
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            margin: '1rem 0 0.5rem 0',
            color: 'grey.700',
            '&:first-of-type': {
              marginTop: 0,
            },
          },
          '& h1': { fontSize: '1.5rem', fontWeight: 600 },
          '& h2': { fontSize: '1.25rem', fontWeight: 600 },
          '& h3': { fontSize: '1.125rem', fontWeight: 600 },
          '& h4': { fontSize: '1rem', fontWeight: 600 },
          '& h5': { fontSize: '0.875rem', fontWeight: 600 },
          '& h6': { fontSize: '0.75rem', fontWeight: 600 },
          '& ul, & ol': {
            margin: '0 0 0.75rem 0',
            paddingLeft: '1.5rem',
          },
          '& li': {
            marginBottom: '0.25rem',
          },
          '& hr': {
            border: 'none',
            borderTop: '1px solid',
            borderColor: 'grey.200',
            margin: '0.75rem 0',
          },
          '& table': {
            borderCollapse: 'collapse',
            marginBottom: '0.75rem',
            width: '100%',
            fontSize: '12px',
          },
          '& td, & th': {
            padding: '0.375rem 0.5rem',
          },
          '& th': {
            fontWeight: 600,
            color: 'grey.600',
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.025em',
          },
          '& thead': {
            borderBottom: '1px solid',
            borderColor: 'grey.300',
          },
          '& tbody tr': {
            borderBottom: '1px solid',
            borderColor: 'grey.100',
            '&:last-child': {
              borderBottom: 'none',
            },
          },
          '& blockquote': {
            borderLeft: '3px solid',
            borderLeftColor: 'grey.300',
            margin: '0.75rem 0',
            paddingLeft: '0.75rem',
            paddingTop: '0.25rem',
            paddingBottom: '0.25rem',
            color: 'grey.600',
            fontStyle: 'italic',
            fontSize: '12px',
          },
          '& code': {
            color: 'primary.main',
            backgroundColor: 'grey.200',
            borderRadius: '3px',
            padding: '0.1em 0.3em',
          },
          '& strong': {
            fontWeight: 600,
            color: 'grey.700',
          },
          '& span.positive': {
            color: 'success.main',
          },
          '& span.negative': {
            color: 'error.main',
          },
        }}
      >
        <SourcesContext.Provider value={sources}>
          <Markdown
            components={markdownComponents}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizationSchema]]}
            remarkPlugins={[remarkGfm]}
          >
            {processedContents}
          </Markdown>
        </SourcesContext.Provider>
      </QBox>
      {(isFeatureEnabled(FEATURE_FLAG_AI_COMP_ADMIN_AGENT) || isFeatureEnabled(FEATURE_FLAG_AGENT_CHARTING)) &&
        message.content &&
        message.content.length > 0 && <ContentBlockRenderer blocks={message.content} />}
      {sources.length > 0 && <SourcesFooter sources={sources} />}
      {onFeedback && (
        <FeedbackButtons
          feedback={feedback ?? null}
          feedbackResponse={feedbackResponse}
          onFeedback={(f) => onFeedback(message.id, f)}
          onFeedbackResponse={onFeedbackResponse ? async (text) => onFeedbackResponse(message.id, text) : undefined}
        />
      )}
    </>
  );
};
